import { useCallback, useEffect, useState } from 'react'
import { Icon } from '@carrent/ui'
import { api } from '@carrent/api'
import { CarChangeList } from './CarChangeList'

/**
 * Вкладка «Ручные изменения» карточки машины.
 *
 * ── Про название ──────────────────────────────────────────────────────
 *
 * Именно «ручные», а не «история». Сюда попадает только то, что человек
 * менял сам; изменения, выведенные системой из операции — старт поездки
 * переводит машину в in_use, завершение записывает пробег, — не входят.
 *
 * Разница видна на пробеге: строка «45 100 → 50 000» при текущем
 * значении 62 300 не противоречие, а разница между правкой и накатанным.
 * Названная «историей», вкладка читалась бы как полная и вводила бы
 * в заблуждение, поэтому под заголовком стоит пояснение, а не подпись
 * «все изменения».
 */

const PER_PAGE = 30

export function CarHistoryTab({ car, dictionaries }) {
  const [items, setItems] = useState([])
  const [meta, setMeta] = useState({ page: 1, pages: 1 })
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const r = await api.get(`/admin/cars/${car.id}/history`, {
        params: { page, per_page: PER_PAGE },
      })
      const data = r.data?.data || {}
      setItems(data.items || [])
      setMeta(data.meta || { page: 1, pages: 1 })
    } catch {
      setError('Не удалось загрузить историю изменений')
    } finally {
      setLoading(false)
    }
  }, [car.id, page])

  useEffect(() => {
    // Промис эффекта подхватывается явно: load ловит свои ошибки сам,
    // но брошенный без обработчика промис — это необработанное
    // отклонение при любой будущей правке load.
    load().catch(() => {})
  }, [load])

  if (loading) {
    return (
      <div className="flex-col gap-sm">
        {Array.from({ length: 4 }, (_, i) => (
          <span key={i} className="skeleton table__skeleton-row" />
        ))}
      </div>
    )
  }

  // Ошибка отделена от пустоты: сорванный запрос, показанный как
  // «машину не редактировали», — прямая неправда о карточке.
  if (error) {
    return (
      <div className="state">
        <p className="state__title">{error}</p>
        <button type="button" className="btn btn--secondary" onClick={load}>
          Повторить
        </button>
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="state">
        <Icon name="clock" size={32} />
        <p className="state__title">Карточку ещё не редактировали</p>
        <p className="state__text">
          Здесь появятся ручные правки: кто менял, когда и что было раньше.
        </p>
      </div>
    )
  }

  return (
    <div className="history">
      <p className="history__note">
        Только ручные правки карточки. Изменения от поездок — статус на выезде
        и пробег по возвращении — сюда не попадают.
      </p>

      <CarChangeList entries={items} dictionaries={dictionaries} />

      {meta.pages > 1 && (
        <div className="pager">
          <button type="button" className="btn btn--ghost btn--sm"
            disabled={page <= 1} onClick={() => setPage(page - 1)}>
            <Icon name="chevronLeft" size={14} />
            Новее
          </button>
          <span className="pager__position">Страница {page} из {meta.pages}</span>
          <button type="button" className="btn btn--ghost btn--sm"
            disabled={page >= meta.pages} onClick={() => setPage(page + 1)}>
            Старее
            <Icon name="chevronRight" size={14} />
          </button>
        </div>
      )}
    </div>
  )
}
